import React, { useState, useMemo } from "react";

import { useLazyQuery, useMutation } from "@apollo/client";
import get from "lodash/get";
import moment from "moment/min/moment-with-locales.min";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import Button from "shared/components/Button";
import Calendar from "shared/components/Calendar";
import Notification from "shared/components/Notification";
import Space from "shared/components/Space";
import Spin from "shared/components/Spin";
import { StepStore } from "shared/components/Tab";
import { PLAN_INTERVENTION } from "shared/graphql/mutations";
import { UPDATE_FOLDER } from "shared/graphql/mutations";
import {
  INTERVENTION_INTERVALS,
  INTERVENTION_DATES,
} from "shared/graphql/queries";
import { DEFAULT_VALUES, NAME_SPACES } from "shared/locals/constants";
import { MOBILE_MAX_WIDTH } from "utils/constants";
import useWindowDimensions from "utils/hooks/useWindowDimensions";

import { PLANING_CONFIRM, PLANING_WARNING } from "../constants";
import Form from "../Form";

import Error from "./Error";
import Intervals from "./Intervals";

import "./styles.scss";

const Component = ({
  step: { changeTab },
  onChange,
  folder,
  folderStatus,
  actionCode,
}) => {
  const { id } = useParams();
  const { width } = useWindowDimensions();
  const { t } = useTranslation(NAME_SPACES.HOME);
  const { t: tError } = useTranslation(NAME_SPACES.ERRORS);
  const [selectedDate, setSelectedDate] = useState();
  const [formDate, setFormData] = useState({});
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);

  const [loadingForm, setLoadingForm] = useState(false);

  const [updateFolder] = useMutation(UPDATE_FOLDER, {
    onError: () => {
      Notification.error({
        duration: DEFAULT_VALUES.NOTIFICATION_DURATION,
        message: tError("GENERIC"),
      });
      setLoadingForm(false);
    },
  });

  const [getDates, datesQuery] = useLazyQuery(INTERVENTION_DATES, {
    fetchPolicy: "no-cache",
    onError: (error) => {
      const _error = get(error.graphQLErrors, "[0].extensions");
      _error.statusCode === 472 && changeTab(PLANING_WARNING);
    },
  });
  const [getIntervals, intervalsQuery] = useLazyQuery(INTERVENTION_INTERVALS, {
    fetchPolicy: "no-cache",
  });

  const [planIntervention, planInterventionQuery] = useMutation(
    PLAN_INTERVENTION,
    {
      onCompleted: () => {
        onChange({
          date: selectedDate,
          interval: selectedTimeSlot,
        });
        changeTab(PLANING_CONFIRM);
      },
      onError: () => {
        setLoadingForm(false);
      },
    },
  );

  const availableDays = useMemo(
    () =>
      get(datesQuery, "data.interventionDates.data", []).map(
        ({ date }) => date,
      ),
    [datesQuery.data],
  );
  const isError = useMemo(
    () => planInterventionQuery.error || datesQuery.error,
    [planInterventionQuery, datesQuery],
  );

  const onWeekChange = (where) => {
    getDates({
      variables: {
        where: {
          folderNum: id,
          ...where,
        },
      },
    });
    setSelectedDate(null);
    setSelectedTimeSlot(null);
  };

  const onSelectedDate = (date) => {
    setSelectedDate(date);
    getIntervals({
      variables: {
        where: {
          folderNum: id,
          date,
        },
      },
    });
  };

  const onSubmit = async () => {
    setLoadingForm(true);
    const data = Object.entries(formDate).reduce(
      (a, [k, v]) => (v == null ? a : ((a[k] = v), a)),
      {},
    );
    await updateFolder({
      variables: {
        where: { folderNum: folder?.folderNum },
        data: { ...data },
      },
    });
    await planIntervention({
      variables: {
        where: {
          folderNum: id,
          date: selectedDate,
        },
        data: selectedTimeSlot,
      },
    });
    setLoadingForm(false);
  };

  return (
    <div className={"spaced-col"}>
      <div>
        <Calendar
          onSelectedDate={onSelectedDate}
          onWeekChange={onWeekChange}
          availableDays={availableDays}
        />
      </div>
      <Space h={40} />
      {datesQuery.loading && (
        <div className={"date-loader-container"}>
          <Spin />
        </div>
      )}
      {isError && !datesQuery.loading && <Error />}
      {!isError && !datesQuery.loading && (
        <>
          {selectedDate ? (
            <>
              <p className={"selected-date"}>
                {moment(selectedDate).format("dddd D MMMM")}
              </p>
              <Space h={16} />
              <Intervals
                query={intervalsQuery}
                selected={selectedTimeSlot}
                onSelect={setSelectedTimeSlot}
              />
            </>
          ) : (
            <Space h={76} />
          )}
        </>
      )}
      {!isError && (
        <>
          <Space h={44} />
          <Form
            folder={folder}
            folderStatus={folderStatus}
            actionCode={actionCode}
            onChange={setFormData}
          />
          <Space h={24} />
        </>
      )}
      <div className={`spaced-row ${width < MOBILE_MAX_WIDTH && "col"}`}>
        {selectedDate && (
          <p
            className={"button-text-back"}
            onClick={() => {
              setSelectedDate(null);
              setSelectedTimeSlot(null);
            }}>
            {t("BACK")}
          </p>
        )}
        <Space sh={12} />
        <Button
          text={t("CONFIRM")}
          loading={loadingForm || planInterventionQuery.loading}
          disabled={isError || !selectedTimeSlot}
          onClick={onSubmit}
        />
      </div>
    </div>
  );
};

Component.propTypes = {
  folder: PropTypes.shape({
    productCode: PropTypes.string,
    folderNum: PropTypes.string.isRequired,
  }),
  step: PropTypes.shape({
    changeTab: PropTypes.func.isRequired,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  folderStatus: PropTypes.shape({
    processCode: PropTypes.string,
    statusCode: PropTypes.string,
  }).isRequired,
  actionCode: PropTypes.string,
};

export default StepStore(Component);
